import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, of, throwError } from 'rxjs';
import { WeatherForecast } from '../data-models/weather-forecast.model';

export interface WeatherData {
  temperature: number;
  humidity: number;
  windSpeed: number;
  windDirection: string;
  /** Human-readable conditions (Clear, Cloudy, Rain, etc.) derived from the
   * WMO weather code. Empty string when no mapping is available. */
  conditions: string;
}

@Injectable({
  providedIn: 'root'
})
export class WeatherService {
  private http = inject(HttpClient);
  private baseHistoricalUrl = 'https://historical-forecast-api.open-meteo.com/v1/forecast';
  private baseCurrentUrl = 'https://api.open-meteo.com/v1/forecast';

  /**
   * Get weather data for a specific location, date, and time
   * Uses Open-Meteo API (free, no API key required)
   *
   * @param lat Latitude
   * @param lon Longitude
   * @param dateString Date in YYYY-MM-DD format
   * @param timeString Time in HH:MM format (24-hour)
   */
  getWeather(lat: number, lon: number, dateString: string, timeString: string): Observable<WeatherData> {
    const targetDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);

    const isToday = targetDate.getTime() === today.getTime();
    const isFuture = targetDate.getTime() > today.getTime();
    const url = isToday || isFuture ? this.baseCurrentUrl : this.baseHistoricalUrl;

    let params: Record<string, string>;

    if (isToday || isFuture) {
      // Current/forecast API
      params = {
        latitude: lat.toString(),
        longitude: lon.toString(),
        hourly: 'temperature_2m,relative_humidity_2m,wind_speed_10m,wind_direction_10m,weather_code',
        forecast_days: '1',
        wind_speed_unit: 'mph',
        temperature_unit: 'fahrenheit',
        precipitation_unit: 'inch',
        past_hours: '24'
      };
    } else {
      // Historical API
      params = {
        latitude: lat.toString(),
        longitude: lon.toString(),
        start_date: dateString,
        end_date: dateString,
        hourly: 'temperature_2m,relative_humidity_2m,wind_speed_10m,wind_direction_10m,weather_code',
        wind_speed_unit: 'mph',
        temperature_unit: 'fahrenheit',
        precipitation_unit: 'inch'
      };
    }

    return this.http.get<any>(url, { params }).pipe(
      map(data => this.extractWeatherData(data, dateString, timeString)),
      catchError(error => {
        console.error('Weather API error:', error);
        throw new Error('Unable to fetch weather data. Please try again.');
      })
    );
  }

  getForecast(lat: number, long: number): Observable<WeatherForecast> {
    const params = {
      latitude: lat.toString(),
      longitude: long.toString(),
      current: 'temperature_2m,relative_humidity_2m,precipitation,wind_speed_10m,wind_direction_10m,weather_code',
      hourly: 'temperature_2m,precipitation_probability,wind_speed_10m,wind_direction_10m,weather_code',
      forecast_hours: '12',
      timezone: 'auto',
      temperature_unit: 'fahrenheit',
      wind_speed_unit: 'mph',
      precipitation_unit: 'inch',
    }

    return this.http.get<any>(this.baseCurrentUrl, { params }).pipe(
      map(data => this.extractForecastData(data)),
      catchError(() => throwError(() => new Error('Unable to fetch weather data. Please try again.')))
    );
  }

  private extractForecastData(data: any): WeatherForecast {
    const { current, hourly } = data;

    return {
      latitude: data.latitude,
      longitude: data.longitude,
      current: {
        temperature: Math.round(current.temperature_2m),
        humidity: Math.round(current.relative_humidity_2m),
        windSpeed: Math.round(current.wind_speed_10m),
        windDirection: this.degreesToCardinal(current.wind_direction_10m),
        conditions: this.codeToConditions(current.weather_code),
        precipitation: current.precipitation,
        observedAt: current.time
      },
      forecast: hourly.time.map((time: string, index: number) => ({
        time,
        temperature: Math.round(hourly.temperature_2m[index]),
        windSpeed: Math.round(hourly.wind_speed_10m[index]),
        windDirection: this.degreesToCardinal(hourly.wind_direction_10m[index]),
        precipitationProbability: hourly.precipitation_probability[index],
        conditions: this.codeToConditions(hourly.weather_code[index])
      }))
    }
  }

  private extractWeatherData(data: any, dateString: string, timeString: string): WeatherData {
    const hourIndex = this.findClosestHourIndex(data, dateString, timeString);
    const code: number | null = data?.hourly?.weather_code?.[hourIndex] ?? null;

    return {
      temperature: Math.round(data?.hourly?.temperature_2m?.[hourIndex] ?? 0),
      humidity: Math.round(data?.hourly?.relative_humidity_2m?.[hourIndex] ?? 0),
      windSpeed: Math.round(data?.hourly?.wind_speed_10m?.[hourIndex] ?? 0),
      windDirection: this.degreesToCardinal(data?.hourly?.wind_direction_10m?.[hourIndex] ?? 0),
      conditions: this.codeToConditions(code)
    };
  }

  /**
   * Maps a WMO weather code (Open-Meteo's `weather_code` hourly field) to a
   * human-readable conditions label. Buckets coarsely — pilots care about
   * "is it raining" more than "drizzle code 53 vs 55". Returns empty string
   * for unknown codes so the caller can choose to keep the user's manual
   * entry instead of overwriting it.
   *
   * WMO codes: https://open-meteo.com/en/docs (weather_code section).
   */
  private codeToConditions(code: number | null): string {
    if (code == null) return '';
    switch (code) {
      case 0: return 'Clear';
      case 1: return 'Mainly clear';
      case 2: return 'Partly cloudy';
      case 3: return 'Overcast';
      case 45:
      case 48: return 'Fog';
      case 51:
      case 53:
      case 55:
      case 56:
      case 57: return 'Drizzle';
      case 61:
      case 63:
      case 65:
      case 66:
      case 67: return 'Rain';
      case 71:
      case 73:
      case 75:
      case 77: return 'Snow';
      case 80:
      case 81:
      case 82: return 'Rain showers';
      case 85:
      case 86: return 'Snow showers';
      case 95: return 'Thunderstorm';
      case 96:
      case 99: return 'Thunderstorm with hail';
      default: return '';
    }
  }

  private findClosestHourIndex(data: any, dateString: string, timeString: string): number {
    if (!data?.hourly?.time?.length) {
      return 0;
    }

    // Parse target datetime
    const [hours, minutes] = timeString.split(':').map(Number);
    const targetDate = new Date(dateString);
    targetDate.setHours(hours, minutes || 0, 0, 0);
    const targetTime = targetDate.getTime();

    let closestIndex = 0;
    let minDiff = Infinity;

    for (let i = 0; i < data.hourly.time.length; i++) {
      const hourlyDate = new Date(data.hourly.time[i]);
      const diff = Math.abs(hourlyDate.getTime() - targetTime);

      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    }

    return closestIndex;
  }

  /**
   * Convert wind direction from degrees to 16-point cardinal direction
   */
  private degreesToCardinal(degrees: number): string {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  }
}
